import { useContext, useEffect, useState, useCallback } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import Dropdown from '../common/Dropdown';
import ProgressBar from '../common/ProgressBar';
import { sidebarData } from '../../config/sidebarData';
import { LogoutIcon, PremiumIcon, SinglePersonIcon } from '../../svg';
import { UserContext } from '../../context/UserContext';
import useDeviceDetect from '../../helpers/screens';
import { AuthService } from '../../services/authService';
import { BrandMemberRole } from '../../types/enums';
import UpgradeRequiredModal from '../Modal/UpgradeRequiredModal';
import { Button } from '../common/Button';
import IconButton from '../common/IconButton';
import { useTranslation } from 'react-i18next';
// Temporarily hidden - force English language
// import LanguageSelector from '../common/LanguageSelector';
import { TFunction } from 'i18next';
import Copyright from '../common/Copyright';
import { useLocation } from 'react-router-dom';
import ImpersonationBadge from '../common/ImpersonationBadge';
import NotificationDropdown from './NotificationDropdown';
import { useProfileCompletionQuery } from '../../hooks/queries/useUserQueries';
import useSubscription from '../../hooks/useSubscription';

const Navbar = () => {
  const navigate = useNavigate();
  const { isMobile } = useDeviceDetect();
  const [expand, setExpand] = useState<boolean>(false);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const userContext = useContext(UserContext);
  const { t } = useTranslation();
  const [openMenus, setOpenMenus] = useState<{ [key: string]: boolean }>({});
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);

  const location = useLocation();

  if (!userContext) {
    throw new Error('useContext must be used within a UserProvider');
  }

  const { user } = userContext;
  const { hasSubscription } = useSubscription();

  const { data: completion = 0 } = useProfileCompletionQuery(user?._id ?? '');

  useEffect(() => {
    if (isMobile && expand) {
      const handleScroll = () => {
        setExpand(false);
      };
      window.addEventListener('scroll', handleScroll);

      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }
  }, [isMobile, expand]);

  const toggleExpand = useCallback(() => {
    setExpand((prevExpand) => !prevExpand);
  }, []);

  const handleBoostClick = () => {
    navigate('/plans');
  };

  const handleCompleteProfileClick = () => {
    navigate('/profile');
  };

  const logout = () => {
    AuthService.logout();
  };

  const handleUpgradeClick = () => {
    setIsUpgradeModalOpen(false);
    navigate('/plans');
  };

  const displayForBrand = (
    val: { access?: string; label?: string } | string,
  ): boolean => {
    if (
      user !== undefined &&
      user !== null &&
      user.brandMemberRole !== BrandMemberRole.User &&
      user.brandMemberRole !== BrandMemberRole.Manager
    )
      return true;
    else if (
      user !== undefined &&
      user !== null &&
      user.brandMemberRole === BrandMemberRole.Manager &&
      typeof val === 'object' &&
      val.label?.toLowerCase() === 'user management'
    ) {
      return true;
    }
    return typeof val === 'object' ? val.access === 'free' : false;
  };

  const displayCompletion = () =>
    completion !== 100 && user && user.role !== 'admin';

  const profileImage = user?.profileImage
    ? user.profileImage
    : '/assets/svgs/fi_avatar.svg';
  const renderProfileDropdown = (t: TFunction<'translation', undefined>) => (
    <Dropdown
      closeOnItemClick={true}
      label={(isOpen) => (
        <div
          className={`w-[32px] h-[32px] rounded-full overflow-hidden ${
            isOpen ? 'border-2 border-solid border-black' : ''
          }`}
        >
          <img
            src={profileImage}
            alt="profile"
            className="w-full h-full object-cover"
          />
        </div>
      )}
      side="right"
    >
      <ul className="w-[150px] flex flex-col items-center bg-white border shadow-lg rounded-lg py-2 sm:translate-x-0 translate-x-[100px]">
        <li className="hover:bg-gray-100 w-full">
          <Link to="/profile" className="flex items-center p-2">
            <SinglePersonIcon className="size-5 mr-2" onClick={() => null} />
            <span>{t('common.profile')}</span>
          </Link>
        </li>
        <li
          className="hover:bg-gray-100 w-full p-2 flex items-center"
          onClick={logout}
        >
          <LogoutIcon className="size-5 mr-2" onClick={undefined} />
          <span>{t('common.logout')}</span>
        </li>
      </ul>
    </Dropdown>
  );

  // const renderNotificationDropdown = (
  //   t: TFunction<'translation', undefined>,
  // ) => {
  //   const [isOpen, setIsOpen] = useState(false);
  //   const modalRef = useRef<HTMLDivElement>(null);

  //   useEffect(() => {
  //     const handleClickOutside = (event: MouseEvent) => {
  //       if (
  //         modalRef.current &&
  //         !modalRef.current.contains(event.target as Node)
  //       ) {
  //         setIsOpen(false);
  //       }
  //     };

  //     if (isOpen) {
  //       document.addEventListener('mousedown', handleClickOutside);
  //     }
  //     return () => {
  //       document.removeEventListener('mousedown', handleClickOutside);
  //     };
  //   }, [isOpen]);

  //   return (
  //     <div ref={modalRef} className="relative">
  //       <IconButton
  //         src="/assets/svgs/fi_bell.svg"
  //         tooltip={String(t('common.notification'))}
  //         onClick={() => {
  //           setIsNotificationOpen((prev) => !prev);
  //           if (!isNotificationOpen) fetchNotifications();
  //         }}
  //       />
  //       {isNotificationOpen && (
  //         <div
  //           className="absolute left-[-290px] top-[40px] mt-2 w-[320px] flex flex-col items-center bg-white border shadow-lg rounded-lg z-50"
  //           onClick={(e) => e.stopPropagation()}
  //         >
  //           <div className="w-full leading-[22px]">
  //             <div className="flex justify-between py-[16px] px-[24px]">
  //               <div className="text-base font-semibold text-[#172B4D]">
  //                 {t('common.notifications')}
  //               </div>
  //               <img
  //                 src="/assets/fi_close_black.svg"
  //                 className="h-[24px] w-[24px] cursor-pointer z-10"
  //                 onClick={(e) => {
  //                   e.stopPropagation();
  //                   setIsNotificationOpen(false);
  //                 }}
  //                 alt="Close"
  //               />
  //             </div>
  //             {notifications.length === 0 && (
  //               <div className="py-[16px] px-[24px] text-xs text-gray-500">
  //                 {t('common.noNotifications')}
  //               </div>
  //             )}
  //             <div className="max-h-[400px] overflow-y-auto">
  //               {notifications.length > 0 &&
  //                 notifications.map((notification, idx) => {
  //                   const n: any = notification;
  //                   const notificationId = n._id || n.id;
  //                   const notificationDate = n.createdAt || n.date;
  //                   const isUnread = n.read === false || n.read === undefined;
  //                   return (
  //                     <div
  //                       key={notificationId}
  //                       className={`py-[16px] px-[24px] mb-2 rounded-lg ${isUnread && idx === 0 ? 'bg-[#E6F8F0]' : 'bg-white'}`}
  //                     >
  //                       <span className="text-[#000000B2] text-xs font-normal ">
  //                         {n.message}
  //                       </span>
  //                       <div className="text-[#9B919D] text-xs font-normal">
  //                         {formatNotificationDate(notificationDate)}
  //                       </div>
  //                     </div>
  //                   );
  //                 })}
  //             </div>
  //           </div>
  //         </div>
  //       )}
  //     </div>
  //   );
  // };

  const toggleMenu = (label: string) => {
    setOpenMenus((prev) => ({ ...prev, [label]: !prev[label] }));
  };
  const isSubmenuActive = (item: (typeof sidebarData)[0]) =>
    item.subMenu?.some((subItem) =>
      location.pathname.startsWith(subItem.route),
    );
  useEffect(() => {
    const newOpenMenus: { [key: string]: boolean } = {};

    sidebarData.forEach((item) => {
      if (item.subMenu && item.subMenu.length > 0) {
        const isSubmenuActive = item.subMenu.some((subItem) =>
          location.pathname.startsWith(subItem.route),
        );
        if (isSubmenuActive) {
          newOpenMenus[item.label] = true;
        }
      }
    });

    setOpenMenus(newOpenMenus);
  }, [location]);

  return (
    <nav
      className={`w-full flex flex-row justify-between items-center z-50 border-b ${!isMobile && 'h-[64px]'}`}
    >
      {!isMobile && (
        <>
          <div className="w-1/5 pr-[24px] pl-[52px]">
            <Link to="/" className="inline-block">
              <img
                src="/assets/Logo.svg"
                className="min-w-[150px] w-[151px] min-h-[40px] h-[32px] object-contain"
                alt="logo"
              />
            </Link>
          </div>
          <div className="w-4/5 flex justify-between px-[24px]">
            {
              <div className="flex flex-col lg:flex-row items-center w-[375px] ">
                {/* Bouton Upgrade Now masqué - désactivé */}
                {/* Code commenté pour masquer définitivement le bouton Upgrade Now
                {user?.role === 'brandUser' ? (
                  location.pathname.includes('my-links') && !hasSubscription ? (
                    <Button onClick={handleBoostClick} color="secondary">
                      {t('common.upgradeNow')}
                    </Button>
                  ) : null
                ) : (
                  !hasSubscription &&
                  displayForBrand('') &&
                  user?.role !== 'admin' && (
                    <Button onClick={handleBoostClick} color="secondary">
                      {t('common.upgradeNow')}
                    </Button>
                  )
                )}
                */}
              </div>
            }
            <div className="flex items-center gap-[10px]">
              <ImpersonationBadge />
              {user?._id && user?.role !== 'admin' && (
                <div className="w-[36px] h-[36px] flex items-center justify-center rounded-full hover:bg-gray-100 has-[.notification-open]:bg-gray-100">
                  <NotificationDropdown t={t} userId={user._id} />
                </div>
              )}

              {user?.role !== 'admin' &&
                user?.brandMemberRole === BrandMemberRole.Admin && (
                  <Link
                    to="/wallet"
                    className={`w-[36px] h-[36px] flex items-center justify-center rounded-full ${
                      location.pathname.startsWith('/wallet')
                        ? 'bg-gray-100'
                        : 'hover:bg-gray-100'
                    }`}
                  >
                    <IconButton
                      src="/assets/svgs/fi_wallet.svg"
                      tooltip={String(t('common.wallet'))}
                    />
                  </Link>
                )}

              {renderProfileDropdown(t)}
              {/* Temporarily hidden - force English language */}
              {/* <LanguageSelector title="Language Select" /> */}
            </div>
          </div>
        </>
      )}

      {isMobile && (
        <div className="w-full px-[12px] sm:px-[20px] rounded-b-md">
          <div
            className={`w-full  h-auto
              // ${expand ? 'h-[680px]' : 'h-[60px]'}
             transition-all ease-in-out duration-500 relative overflow-hidden bg-white`}
          >
            <div className="flex justify-between items-center h-[68px] w-full">
              <Link to="/">
                <IconButton
                  src="/assets/Logo.svg"
                  hoverSrc="/assets/svgs/fi_avatar_hover.svg"
                  tooltip={String(t('common.profile'))}
                />
              </Link>
              {user?.role === 'brandUser'
                ? null
                : !hasSubscription &&
                  displayForBrand('') && (
                    <Button onClick={handleBoostClick} color="secondary">
                      {t('common.upgradeNow')}
                    </Button>
                  )}
              <button onClick={toggleExpand} className="size-6 cursor-pointer">
                {expand ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="size-6 rounded-md"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6 18 18 6M6 6l12 12"
                    />
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="size-6"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
                    />
                  </svg>
                )}
              </button>
            </div>
            {expand && (
              <div className="mt-4">
                <ImpersonationBadge />
                <div className="flex items-center gap-3">
                  {user?._id && user?.role !== 'admin' && (
                    <NotificationDropdown t={t} userId={user._id} />
                  )}

                  {user?.role !== 'admin' &&
                    user?.brandMemberRole === BrandMemberRole.Admin && (
                      <Link to="/wallet">
                        <IconButton
                          src="/assets/svgs/fi_wallet.svg"
                          tooltip={String(t('common.wallet'))}
                        />
                      </Link>
                    )}

                  {renderProfileDropdown(t)}
                  {/* Temporarily hidden - force English language */}
                  {/* <LanguageSelector title="language select" side="left" /> */}
                </div>
                <ul className="mt-4">
                  {sidebarData.map((val, index) => {
                    const hasSubMenu = !!val.subMenu?.length;
                    const isOpen = openMenus[val.label];

                    return (
                      <li key={index}>
                        {val.route &&
                        user?.role &&
                        val.type.includes(user.role) &&
                        displayForBrand(val) ? (
                          !hasSubMenu ? (
                            <NavLink
                              to={val.route}
                              onClick={toggleExpand}
                              className={({ isActive }) =>
                                `flex items-center cursor-pointer py-[12px] px-2  font-content ${
                                  isActive
                                    ? 'bg-gray-100 text-[#020D3A] border-l-4 border-[#020D3A]'
                                    : 'bg-transparent text-[#4D494F]'
                                }`
                              }
                            >
                              <img
                                src={val?.icon}
                                alt={val?.label}
                                className="ml-[10px] mr-[12px]"
                              />
                              <div className="flex items-center justify-between w-full">
                                <div>
                                  <span className="whitespace-nowrap">
                                    {t(
                                      `common.${val.label.toLowerCase().replace(/\s/g, '')}`,
                                    )}
                                  </span>
                                </div>
                                <div>
                                  {val.access === 'limited' &&
                                    !hasSubscription && <PremiumIcon />}
                                </div>
                              </div>
                            </NavLink>
                          ) : (
                            <div
                              onClick={() => toggleMenu(val.label)}
                              className={`flex items-center cursor-pointer py-[12px] px-2 font-content ${
                                isSubmenuActive(val)
                                  ? 'bg-gray-100 text-[#020D3A] border-l-4 border-[#020D3A]'
                                  : 'bg-transparent text-[#4D494F]'
                              }`}
                            >
                              <img
                                src={val?.icon}
                                alt={val?.label}
                                className="ml-[10px] mr-[12px]"
                              />
                              <div className="flex items-center justify-between w-full">
                                <div>
                                  <span className="whitespace-nowrap">
                                    {t(
                                      `common.${val.label.toLowerCase().replace(/\s/g, '')}`,
                                    )}
                                  </span>
                                </div>
                                <div>
                                  {isOpen ? (
                                    <img
                                      src="/assets/fichevron-up.svg"
                                      alt="dashboard"
                                      className={`ml-[10px] mr-[12px]`}
                                    />
                                  ) : (
                                    <img
                                      src="/assets/fichevron-down.svg"
                                      alt="dashboard"
                                      className={`ml-[10px] mr-[12px]`}
                                    />
                                  )}
                                </div>
                              </div>
                            </div>
                          )
                        ) : (
                          user &&
                          !val.route &&
                          val.type.includes(user.role) &&
                          displayForBrand(val) && (
                            <div className="flex items-center cursor-pointer py-[12px] rounded-full font-content bg-transparent text-[#afafaf]">
                              <img
                                src={val?.icon}
                                alt={val?.label}
                                className="ml-[10px] mr-[12px]"
                              />
                              <div className="lg:flex">
                                <span className="mr-2">
                                  {t(
                                    `common.${val.label.toLowerCase().replace(/\s/g, '')}`,
                                  )}
                                </span>
                                <span className="text-secondary font-bold">
                                  {t('common.soon')}
                                </span>
                              </div>
                            </div>
                          )
                        )}
                        {hasSubMenu && isOpen && (
                          <ul className="submenu-list">
                            {val.subMenu?.map((subItem, subIdx) => (
                              <li key={subIdx}>
                                <NavLink
                                  to={subItem.route}
                                  onClick={toggleExpand}
                                  className={({ isActive }) =>
                                    `flex items-center cursor-pointer py-[12px] px-2 font-content ${
                                      isActive
                                        ? 'bg-gray-100 text-[#020D3A] '
                                        : 'bg-transparent text-[#4D494F]'
                                    }`
                                  }
                                >
                                  <img
                                    src={subItem?.icon}
                                    alt={subItem?.label}
                                    className="ml-[10px] mr-[12px]"
                                  />
                                  <div className="flex items-center justify-between w-full">
                                    <div>
                                      <span className="whitespace-nowrap">
                                        {t(
                                          `common.${subItem.label.toLowerCase().replace(/\s/g, '')}`,
                                        )}
                                      </span>
                                    </div>
                                    {/* <div>
                                      {subItem.access === 'limited' &&
                                        !hasSubscription && <PremiumIcon />}
                                    </div> */}
                                  </div>
                                </NavLink>
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    );
                  })}
                </ul>
                {user?.role === 'admin' || user?.role === 'brandUser' ? (
                  <div className="p-[24px] flex items-center gap-4">
                    <IconButton
                      tooltip={String(t('common.profile'))}
                      src={profileImage}
                      hoverSrc={profileImage}
                      style={{ height: 51, width: 51 }}
                      className="rounded-full overflow-hidden"
                    />
                    <div>
                      <div>
                        <span className="text-[20px] mt-[10px] ">
                          {user?.email?.split('@')[0]}
                        </span>
                      </div>
                      <div>
                        <span className="text-[14px] mt-[10px] text-[#4F4949] underline capitalize">
                          {user?.role}
                        </span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    {displayCompletion() && (
                      <div className="bg-[#F0F5FF] p-[24px] rounded-lg mt-4">
                        <h1 className="font-header text-[24px] font-[700]">
                          {t('profile.greeting', { name: user?.firstName })}
                        </h1>
                        <span className="text-[14px] mt-[10px] leading-none font-content">
                          {t('profile.incompleteProfile')}
                        </span>
                        <div className="my-[24px]">
                          <ProgressBar completed={completion} />
                        </div>
                        <Button
                          className="w-full"
                          variant="outlined"
                          onClick={handleCompleteProfileClick}
                        >
                          {t('profile.completeProfile')}
                        </Button>
                      </div>
                    )}
                    <Copyright />
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      <UpgradeRequiredModal
        isOpen={isUpgradeModalOpen}
        onClose={() => setIsUpgradeModalOpen(false)}
        onUpgrade={handleUpgradeClick}
      />
    </nav>
  );
};

export default Navbar;
